import { lazy, Suspense } from "react";
import { MapPin, Search, X } from "lucide-react";
import type {
  PublicMapArea,
  PublicPlaceCandidate,
  PublicWaterbodyGeometryCandidate,
} from "@natural/public-contract";
import { relationLabels } from "../../account";
import type { OrganizationAreaClaimsController } from "../hooks/useOrganizationAreaClaims";

const LazyPublicMap = lazy(() =>
  import("../../../PublicMap").then((module) => ({ default: module.PublicMap })),
);

function PublicMap(props: {
  areas: PublicMapArea[];
  selected: PublicPlaceCandidate | null;
  candidates?: PublicPlaceCandidate[];
  onSelect?: (candidate: PublicPlaceCandidate) => void;
  compact?: boolean;
  selectedAreaIds?: string[];
  focusAreaId?: string | null;
  onAreaSelect?: (area: PublicMapArea) => void;
  waterbodyPreviews?: PublicWaterbodyGeometryCandidate[];
}) {
  return (
    <Suspense fallback={<div className="application-map-loading">Ladataan aluekarttaa…</div>}>
      <LazyPublicMap {...props} />
    </Suspense>
  );
}

type OrganizationAreaClaimsStepProps = {
  areas: PublicMapArea[];
  controller: OrganizationAreaClaimsController;
  defaultRelationship: string;
};

export function OrganizationAreaClaimsStep({
  areas,
  controller,
  defaultRelationship,
}: OrganizationAreaClaimsStepProps) {
  const {
    activeClaimIndex,
    addAreaClaim,
    areaDetailsOpen,
    areaKinds,
    areaMatchesOpen,
    areaSearch,
    claimCount,
    focusAreaId,
    matchingAreas,
    parentAreaIds,
    placeCandidates,
    placeSearchState,
    removeAreaClaim,
    searchPlaces,
    selectArea,
    selectedAreaIds,
    selectedPlaces,
    selectPlace,
    setActiveClaimIndex,
    setAreaDetailsOpen,
    setAreaKinds,
    setAreaMatchesOpen,
    setAreaSearch,
    setParentAreaIds,
    setPlaceCandidates,
    setPlaceSearchState,
    setSelectedAreaIds,
    waterbodyPreviews,
    waterbodyPreviewStates,
  } = controller;
  return (
          <fieldset className="wide-field application-area-step">
            <legend>4. Alueet ja toimivalta</legend>
            <p className="form-hint">
              Valitse julkaistu Core-alue suoraan kartalta. Oranssi rajaus on
              valittu Core-alue; vihreä piste on MML:stä valittu uuden alueen
              tai vesistön lähtöpaikka.
            </p>
            <div className="application-area-search">
              <Search />
              <input
                type="search"
                value={areaSearch}
                onChange={(event) => {
                  setAreaSearch(event.target.value);
                  setAreaMatchesOpen(true);
                  setPlaceCandidates([]);
                  setPlaceSearchState("");
                }}
                placeholder="Hae järveä, jokea tai kalatalousaluetta"
                aria-label="Hae julkaistuja Core-alueita"
              />
              {areaSearch && (
                <button
                  type="button"
                  onClick={() => {
                    setAreaSearch("");
                    setAreaMatchesOpen(true);
                    setPlaceCandidates([]);
                    setPlaceSearchState("");
                  }}
                  aria-label="Tyhjennä aluehaku"
                >
                  <X />
                </button>
              )}
              {areaMatchesOpen && matchingAreas.length > 0 && (
                <div className="application-area-results">
                  {matchingAreas.map((area) => (
                    <button
                      type="button"
                      key={area.id}
                      onClick={() => {
                        selectArea(area);
                        setAreaSearch("");
                      }}
                    >
                      <MapPin />
                      <span>
                        <b>{area.name}</b>
                        <small>
                          {area.kind === "river"
                            ? "Joki"
                            : area.kind === "waterbody"
                              ? "Vesistö"
                              : area.kind === "parent"
                                ? "Yläalue"
                                : "Alue"}
                        </small>
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className="application-area-mml-action"
                    disabled={placeSearchState === "loading"}
                    onClick={searchPlaces}
                  >
                    <Search />
                    <span>
                      <b>{placeSearchState === "loading" ? "Haetaan MML-paikkoja…" : "Hae sama nimi MML:stä"}</b>
                      <small>Kun oikeaa järveä, jokea tai aluetta ei ole vielä Coressa</small>
                    </span>
                  </button>
                </div>
              )}
            </div>
            <div className="application-place-discovery">
              <button type="button" disabled={areaSearch.trim().length < 2 || placeSearchState === "loading"} onClick={searchPlaces}>
                <Search /> {placeSearchState === "loading" ? "Haetaan MML-paikkoja…" : "Hae uusi alue tai vesistö MML:stä"}
              </button>
              {placeSearchState === "empty" && <span>MML ei löytänyt tällä nimellä paikkaehdokkaita.</span>}
              {placeSearchState === "error" && <span>Paikkahaku epäonnistui turvallisesti. Yritä uudelleen.</span>}
              {placeCandidates.length > 0 && <div className="application-place-results">{placeCandidates.map((place) => <button type="button" key={`${place.provider}:${place.placeId}`} onClick={() => selectPlace(place)}><MapPin/><span><b>{place.name}</b><small>{place.municipalityName ?? "Kunta ei tiedossa"} · {place.placeTypeLabel ?? "Paikka"}</small></span></button>)}</div>}
            </div>
            <div className="application-area-map">
              <PublicMap
                areas={areas}
                candidates={selectedPlaces.filter((place): place is PublicPlaceCandidate => Boolean(place))}
                selected={selectedPlaces[activeClaimIndex] ?? null}
                onSelect={selectPlace}
                compact
                selectedAreaIds={selectedAreaIds.filter(Boolean)}
                focusAreaId={focusAreaId}
                onAreaSelect={selectArea}
                waterbodyPreviews={waterbodyPreviews.filter((item): item is PublicWaterbodyGeometryCandidate => Boolean(item))}
              />
              <aside>
                <b>
                  Valitut alueet (
                  {selectedAreaIds.filter(Boolean).length +
                    selectedPlaces.filter(Boolean).length}
                  )
                </b>
                {Array.from({ length: claimCount }, (_, index) => {
                  const area = areas.find(
                    (item) => item.id === selectedAreaIds[index],
                  );
                  return (
                    <article
                      key={index}
                      className={area || selectedPlaces[index] ? "selected" : "empty"}
                      onClick={() => setActiveClaimIndex(index)}
                    >
                      <MapPin />
                      <span>
                        <strong>
                          {area?.name ?? selectedPlaces[index]?.name ?? `Aluevaatimus ${index + 1}`}
                        </strong>
                        <small>
                          {area
                            ? "Julkaistu Core-alue"
                            : selectedPlaces[index] ? `${waterbodyPreviewStates[index] || "Uusi MML-paikka"} · Platform tarkistaa ennen julkaisua` : "Valitse alue kartalta tai hae MML:stä"}
                        </small>
                      </span>
                      {(area || selectedPlaces[index]) && (
                        <button
                          type="button"
                          onClick={() => removeAreaClaim(index)}
                          aria-label={`Poista ${area?.name ?? selectedPlaces[index]?.name}`}
                        >
                          <X />
                        </button>
                      )}
                    </article>
                  );
                })}
                <button
                  type="button"
                  className="manual-area-button"
                  onClick={() => { setAreaDetailsOpen(true); document.querySelector<HTMLInputElement>(".application-area-search input")?.focus(); }}
                >
                  Lisää uusi alue tai vesistö nimellä
                </button>
              </aside>
            </div>
            <details
              className="area-details"
              id="area-details"
              open={areaDetailsOpen}
              onToggle={(event) => setAreaDetailsOpen(event.currentTarget.open)}
            >
              <summary>Aluevaatimusten tarkat tiedot</summary>
              {Array.from({ length: claimCount }, (_, index) => {
                const selectedArea = areas.find(
                  (area) => area.id === selectedAreaIds[index],
                );
                const selectedPlace = selectedPlaces[index];
                return (
                  <div className="area-claim-editor" key={index}>
                    <header>
                      <b>{selectedArea?.name ?? selectedPlace?.name ?? `Aluevaatimus ${index + 1}`}</b>
                      {claimCount > 1 && (
                        <button
                          type="button"
                          onClick={() => removeAreaClaim(index)}
                        >
                          Poista
                        </button>
                      )}
                    </header>
                    <div className="application-field-grid">
                      <label>
                        Alueen nimi
                        <input
                          key={selectedArea?.id ?? selectedPlace?.placeId ?? `manual-${index}`}
                          name={`claim.${index}.requestedAreaName`}
                          minLength={2}
                          maxLength={200}
                          required
                          defaultValue={selectedArea?.name ?? selectedPlace?.name ?? ""}
                          placeholder="Esim. Kivesjärven osakaskunnan vesialue"
                        />
                      </label>
                      <label>
                        Kunta
                        <input
                          name={`claim.${index}.municipality`}
                          minLength={2}
                          maxLength={120}
                          required
                          defaultValue={selectedPlace?.municipalityName ?? ""}
                        />
                      </label>
                      <label>
                        Kohteen tyyppi
                        <select name={`claim.${index}.areaKind`} value={areaKinds[index] ?? "area"} onChange={(event) => setAreaKinds((current) => current.map((value, itemIndex) => itemIndex === index ? event.target.value : value))}>
                          <option value="fishery_area">Kalatalousalue tai yläalue</option>
                          <option value="waterbody">Järvi tai muu vesistö</option>
                          <option value="river">Joki, puro tai kanava</option>
                          <option value="ownership_area">Omistus- tai osakaskunta-alue</option>
                          <option value="permit_area">Lupa-alue</option>
                          <option value="area">Muu alue</option>
                        </select>
                      </label>
                      <label>
                        Yläalue <small>valinnainen</small>
                        <select name={`claim.${index}.parentCoreAreaId`} value={parentAreaIds[index] ?? ""} onChange={(event) => setParentAreaIds((current) => current.map((value, itemIndex) => itemIndex === index ? event.target.value : value))}>
                          <option value="">Ei yläaluetta</option>
                          {areas.filter((area) => area.id !== selectedAreaIds[index]).map((area) => <option value={area.id} key={area.id}>{area.name}</option>)}
                        </select>
                      </label>
                      <input type="hidden" name={`claim.${index}.placeCandidate`} value={selectedPlace ? JSON.stringify(selectedPlace) : ""}/>
                      <label>
                        Olemassa oleva Core-alue
                        <select
                          name={`claim.${index}.coreAreaId`}
                          value={selectedAreaIds[index] ?? ""}
                          onChange={(event) => {
                            const area = areas.find(
                              (item) => item.id === event.target.value,
                            );
                            if (area) selectArea(area);
                            else
                              setSelectedAreaIds((current) =>
                                current.map((value, itemIndex) =>
                                  itemIndex === index ? "" : value,
                                ),
                              );
                          }}
                        >
                          <option value="">Ei vielä tai en ole varma</option>
                          {areas.map((area) => (
                            <option value={area.id} key={area.id}>
                              {area.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Suhde alueeseen
                        <select
                          name={`claim.${index}.relationship`}
                          defaultValue={defaultRelationship}
                        >
                          {Object.entries(relationLabels).map(
                            ([value, label]) => (
                              <option value={value} key={value}>
                                {label}
                              </option>
                            ),
                          )}
                        </select>
                      </label>
                      <label className="wide-field">
                        Oikeusperuste
                        <textarea
                          name={`claim.${index}.claimBasis`}
                          minLength={4}
                          maxLength={1000}
                          required
                          rows={2}
                          placeholder="Omistus, hoitosopimus, päätös tai myyntivaltuus"
                        />
                      </label>
                      <label>
                        Todiste (HTTPS)
                        <input
                          name={`claim.${index}.evidenceUrl`}
                          type="url"
                          pattern="https://.*"
                          required
                        />
                      </label>
                      <label>
                        Kiinteistö-/rekisteriviite <small>valinnainen</small>
                        <input
                          name={`claim.${index}.propertyReference`}
                          maxLength={200}
                        />
                      </label>
                      <label>
                        Voimassa alkaen <small>valinnainen</small>
                        <input name={`claim.${index}.validFrom`} type="date" />
                      </label>
                      <label>
                        Voimassa asti <small>valinnainen</small>
                        <input name={`claim.${index}.validTo`} type="date" />
                      </label>
                      <label className="wide-field">
                        Rajaus ja lisätiedot <small>valinnainen</small>
                        <textarea
                          name={`claim.${index}.scopeNote`}
                          maxLength={1000}
                          rows={2}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                disabled={claimCount >= 10}
                onClick={addAreaClaim}
              >
                Lisää toinen alue tai valtuus
              </button>
            </details>
          </fieldset>
  );
}
